import { useMemo, useState } from "react";
import {
  formatRawStatus,
  resolveFulfillmentState,
} from "@/features/ordini/components/fulfillmentSteps";
import { FORMA_BADGE, pieno } from "@/features/ordini/components/statoToni";
import { TrackingHistoryDialog } from "./TrackingHistoryDialog";
import type { ParcelInfo } from "./ContextPanel";

interface TrackingTimelineProps {
  /** Status Poleepo grezzo dell'ordine (deriva lo step corrente della pill). */
  statusRaw: string | null | undefined;
  carrier: string;
  trackingCode: string;
  /** Colli SDA multicollo. La pill usa solo i ``code``, passati al popup. */
  parcels: ParcelInfo[];
  /** Apre un URL nel browser di sistema (openExternalUrl del ContextPanel). */
  onOpenExternal: (url: string) => void;
  /** "panel" (default, ContextPanel Messaggi: eyebrow + margini),
   *  "inline" (pill sola, senza eyebrow) oppure "dot" (card lista, parity
   *  mockup 2026-07-13: SOLO puntino colorato + label 10.5px — niente pill
   *  bordata; stesso click → popup storico). */
  variant?: "panel" | "inline" | "dot";
  /** Carica i codici colli on-demand al PRIMO click (card ordini:
   *  GET /orders/{ref}/parcels — estrazione lazy dal PDF LDV). In caso di
   *  errore il popup si apre comunque coi ``parcels`` statici. */
  loadParcelCodes?: () => Promise<string[]>;
  /** Ultimo evento REALE del corriere (cacheato dal job orario
   *  tracking_events_pull — Davide 2026-07-03): quando presente la pill
   *  mostra questo al posto del lifecycle Poleepo, e diventa VERDE a
   *  spedizione consegnata. */
  lastCourierEvent?: { label: string; delivered: boolean; ritornoMittente?: boolean } | null;
}

/**
 * Pill INLINE dell'ultimo stato spedizione + popup "Storico spedizione"
 * (#7 2026-06-29).
 *
 * - La PILL mostra l'ultimo stato del LIFECYCLE ORDINE (label dello step
 *   corrente dal mapping condiviso col FulfillmentStepper). E' volutamente
 *   ECONOMICA: deriva tutto da ``order.status`` gia' in memoria, NON chiama
 *   il corriere al mount del ContextPanel (sarebbe una fetch per ogni chat
 *   aperta). Per stati non-lineari/sconosciuti mostra lo stato grezzo formattato.
 * - La pill e' un BOTTONE: il click apre il popup, che a quel punto (e solo
 *   allora) recupera gli EVENTI DI TRACKING REALI SDA da Bracchi via
 *   ``GET /api/tracking``.
 */
export function TrackingTimeline({
  statusRaw,
  carrier,
  trackingCode,
  parcels,
  onOpenExternal,
  variant = "panel",
  loadParcelCodes,
  lastCourierEvent,
}: TrackingTimelineProps) {
  const [open, setOpen] = useState(false);
  // Colli caricati on-demand (null = mai caricati / caricamento fallito →
  // si usano i ``parcels`` statici).
  const [loadedCodes, setLoadedCodes] = useState<string[] | null>(null);
  const state = resolveFulfillmentState(statusRaw);
  // Etichetta: l'ultimo evento REALE del corriere quando cacheato (job
  // orario), altrimenti lo step del lifecycle Poleepo; in mancanza lo stato
  // grezzo formattato.
  const lastLabel =
    lastCourierEvent?.label ||
    state.currentLabel ||
    (state.status ? formatRawStatus(state.status) : "Stato non disponibile");
  // La pill prende l'accento solo quando c'e' un avanzamento vero (step
  // mappato): per stati non-lineari resta neutra ma comunque cliccabile.
  const isTracked = lastCourierEvent != null || state.currentLabel !== null;
  // Consegnata → pill VERDE (Davide 2026-07-03); ritorno al mittente → ROSSA.
  const isDelivered = lastCourierEvent?.delivered === true;
  const isRitorno = lastCourierEvent?.ritornoMittente === true;

  // Codici dei colli per il popup (solo i ``code``). I colli caricati
  // on-demand (card ordini) vincono sugli statici.
  const parcelCodes = useMemo(
    () => loadedCodes ?? parcels.map((p) => p.code).filter(Boolean),
    [loadedCodes, parcels],
  );

  async function handleOpen() {
    if (loadParcelCodes && loadedCodes === null) {
      try {
        setLoadedCodes(await loadParcelCodes());
      } catch {
        // Best-effort: senza colli il popup mostra comunque la LDV master.
      }
    }
    setOpen(true);
  }

  // Variante "dot" (card lista): lo stato del CORRIERE come etichetta di stato
  // (Davide 2026-09-23: «tutti i colori nello stile delle etichette»). Senza
  // eventi reali NON mostriamo il lifecycle ordine ("Pagato" sotto Spedizione
  // confondeva — feedback Davide 2026-07-13) ma un onesto "In attesa eventi".
  // Pieno blu = in viaggio (il blu di «spedito»), pieno verde = consegnata,
  // contorno neutro = nessun evento ancora.
  if (variant === "dot") {
    const hasCourierEvent = lastCourierEvent != null;
    const dotLabel = hasCourierEvent ? lastCourierEvent.label : "In attesa eventi";
    // Ritorno al mittente: rosso, prima di tutto (Davide 26/10).
    const colore = isRitorno
      ? pieno("ritorno-mittente")
      : isDelivered
        ? pieno("consegnata")
        : hasCourierEvent
          ? pieno("spedito")
          : undefined;
    return (
      <>
        <button
          type="button"
          onClick={() => void handleOpen()}
          aria-haspopup="dialog"
          aria-label={`Apri lo storico spedizione — stato: ${dotLabel}`}
          title="Apri lo storico spedizione"
          style={colore}
          className={[
            FORMA_BADGE,
            "max-w-full cursor-pointer self-start transition-[filter] hover:brightness-110",
            colore ? "" : "border-line-2 bg-card text-ink-3",
          ].join(" ")}
        >
          <span className="truncate">{dotLabel}</span>
        </button>
        {open && (
          <TrackingHistoryDialog
            carrier={carrier}
            trackingCode={trackingCode}
            parcelCodes={parcelCodes}
            onOpenExternal={onOpenExternal}
            onClose={() => setOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <div className={variant === "inline" ? "" : "mb-1"}>
      {variant === "panel" && (
        <div className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-ink-3">
          Spedizione
        </div>
      )}
      <button
        type="button"
        onClick={() => void handleOpen()}
        aria-haspopup="dialog"
        aria-label={`Apri lo storico spedizione — stato: ${lastLabel}`}
        title="Apri lo storico spedizione"
        className={[
          "inline-flex max-w-full items-center gap-2 rounded-sm border px-2.5 py-1.5 text-left transition-colors",
          isRitorno
            ? "border-red bg-red-soft hover:border-red"
            : isDelivered
              ? "border-green/40 bg-green-soft hover:border-green"
              : isTracked
                ? "border-accent/30 bg-accent-soft hover:border-accent"
                : "border-line bg-paper hover:border-ink-4",
        ].join(" ")}
      >
        <span
          className={[
            "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
            isRitorno ? "bg-red" : isDelivered ? "bg-green" : isTracked ? "bg-accent" : "bg-ink-4",
          ].join(" ")}
          aria-hidden="true"
        />
        <span
          className={[
            "truncate text-[12px] font-medium",
            isRitorno
              ? "font-bold text-ink"
              : isDelivered
                ? "text-green"
                : isTracked
                  ? "text-accent-2"
                  : "text-ink-2",
          ].join(" ")}
        >
          {lastLabel}
        </span>
        <ChevronIcon />
      </button>
      {open && (
        <TrackingHistoryDialog
          carrier={carrier}
          trackingCode={trackingCode}
          parcelCodes={parcelCodes}
          onOpenExternal={onOpenExternal}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0 fill-none stroke-current stroke-[1.6] text-ink-3 [stroke-linecap:round] [stroke-linejoin:round]"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
